import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Tab,
  Tabs,
  Button,
} from "@mui/material";

import { RootState } from "../store/Index";
import { useSelector } from "react-redux";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import AssetGrid from "././AssetGrid";
import AddAssetDrawer from "././AddAssetDrawer";
import apiService from "../service/Api";

// ASSET IMAGES (fallback)
import AssetTools1 from "../assets/assetsTools1.png";

export interface ToolAsset {
  id: number;
  file: string | null;
  image?: string | null;
  video?: string | null;
  asset_type:
    | "brand_asset"
    | "logo"
    | "lower_third"
    | "image"
    | "custom"
    | "video";
}

const ToolsAssetsDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [tabValue, setTabValue] = useState(0);
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState<number[]>([]);

  const [assets, setAssets] = useState<ToolAsset[]>([]);

  // 🔹 GET TOOLS & ASSETS
  useEffect(() => {
    if (!open) return;

    const fetchAssets = async () => {
      try {
        const response = await apiService.get(
          "/api/configurations/tools-assets/",
        );
        const data = response.data as ToolAsset[];
        setAssets(data);
      } catch (error) {
        console.error("Failed to fetch tools & assets", error);
        // fallback: create dummy assets with type
        const fallbackAssets: ToolAsset[] = Array(11)
          .fill(AssetTools1)
          .map((file, index) => ({
            id: index,
            file,
            asset_type: "brand_asset",
          }));
        setAssets(fallbackAssets);
      }
    };

    fetchAssets();
  }, [open]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCloseMainDrawer = () => {
    setSelectMode(false);
    setSelectedImages([]);
    onClose();
  };
  const tabToTypeMap: Record<number, ToolAsset["asset_type"]> = {
    0: "brand_asset",
    1: "logo",
    2: "lower_third",
    3: "image",
    4: "custom",
    5: "video",
  };
  const filteredAssets = assets.filter(
    (asset) => asset.asset_type === tabToTypeMap[tabValue],
  );
  const downloadAsset = async (url: string, fileName: string) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // free memory
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Failed to download image", err);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseMainDrawer}
        PaperProps={{
          sx: {
            width: { xs: "80%", sm: 400 },
            p: 3,
            backgroundColor: reduxTheme.neutral_colors?.[800],
            color: reduxTheme.primary_colors?.[700],
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h6"
            fontWeight="bold"
            fontSize={25}
            sx={{ mt: 2 }}
          >
            Tools & Assets
          </Typography>
          <IconButton
            aria-label="Add asset"
            onClick={() => setAddDrawerOpen(true)}
          >
            <AddCircleOutlineIcon sx={{ mt: 2, fontSize: 32 }} />
          </IconButton>
        </Box>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="inherit"
          indicatorColor="primary"
          sx={{
            mb: 2,
            mt: 2,
            minHeight: 36,
            ".MuiTab-root": {
              minHeight: 36,
              minWidth: 0,
              paddingX: 0.5,
              flex: 1,
              fontSize: "0.65rem",
              textTransform: "none",
              whiteSpace: "nowrap",
            },
          }}
        >
          <Tab label="Brand Assets" />
          <Tab label="Logos" />
          <Tab label="Lower Thirds" />
          <Tab label="Images" />
          <Tab label="Custom" />
          <Tab label="Videos" />
        </Tabs>

        <Box sx={{ mt: 2 }}>
          <AssetGrid
            assets={filteredAssets
              .map((a) => {
                const url = a.file || a.image || a.video;
                if (!url) return null;
                return {
                  id: a.id,
                  url,
                  type: a.video ? "video" : "image", // add type here
                };
              })
              .filter(
                (
                  a,
                ): a is { id: number; url: string; type: "image" | "video" } =>
                  Boolean(a),
              )}
            selectMode={selectMode}
            setSelectMode={setSelectMode}
            selectedImages={selectedImages}
            setSelectedImages={setSelectedImages}
            onDelete={(id) =>
              setAssets((prev) => prev.filter((asset) => asset.id !== id))
            }
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              sx={{
                borderRadius: 2,
                mt: 2,
                color: reduxTheme.primary_colors?.[900],
                bgcolor: reduxTheme.neutral_colors?.[400],
                textTransform: "none",
                width: 120,
              }}
              onClick={() => {
                const safeUrls = filteredAssets
                  .map((a) => a.file || a.image || a.video)
                  .filter((url): url is string => Boolean(url));

                const imagesToDownload =
                  selectedImages.length > 0
                    ? selectedImages.map((i) => safeUrls[i])
                    : safeUrls;

                imagesToDownload.forEach(async (img, index) => {
                  const fileName = img.split("/").pop() || `image-${index}.png`;
                  await downloadAsset(img, fileName);
                });
              }}
            >
              Download All
            </Button>
          </Box>
        </Box>
      </Drawer>

      <AddAssetDrawer
        open={addDrawerOpen}
        onClose={() => setAddDrawerOpen(false)}
        onAssetAdded={(newAsset: ToolAsset) =>
          setAssets((prev) => [...prev, newAsset])
        }
      />
    </>
  );
};

export default ToolsAssetsDrawer;
